import Vue from "vue";
import { CustomerServiceModel } from "@/models/CsAgentManagementModel";
import {
  CallRoomController,
  CallRoomWhatsappController,
} from "@/models/InboundCall/CallRoomControllerModel";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import vuetify from "@/plugins/vuetify";
import { SnackbarState } from "@/components/customerService/dialog/BulkTicketUpdateDialogComponent/types";
import { DEFAULT_ACTIVE_CALL_STATE, ERROR_REASON } from "./constants";
import { CallSessionStatus } from "@/models/InboundCall/CallSessionModel";
import { RoomType } from "@/models/InboundCall/CallRoomModel";
import { OutboundCallController } from "@/models/InboundCall/OutboundCallController";
import { OutboundCallControllerAdapter } from "@/models/InboundCall/OutboundCallControllerAdapter";
const { i18n } = vuetify;

/**
 * @typedef {Object} ActiveCallState
 * @property {CallRoomController[]} activeCalls - List of currently active call sessions
 * @property {boolean} isTransferringCall - Indicates if a call transfer is currently in progress
 * @property {CustomerServiceModel[]} availableAgents - List of available customer service agents for transfer
 */
type State = {
  listOfActiveCalls: CallRoomController[];
  isTransferringCall: boolean;
  listOfCustomerService: CustomerServiceModel[];
  snackbarState: SnackbarState;
  interruptedCalls: CallRoomController[];
  isLoadingAnswerOrRejectCall: boolean;
  outboundCalls: Record<string, OutboundCallController>;
  outboundCallLoadingStates: Record<string, boolean>;
  outboundCallAdapters: Record<string, OutboundCallControllerAdapter>;
};

const state: State = {
  ...DEFAULT_ACTIVE_CALL_STATE,
};

const getters: GetterTree<State, any> = {
  /**
   * Get list of active calls
   * @param {State} state - Current state
   * @returns {CallRoomController[]} List of active call sessions
   */
  getListOfActiveCalls: (state) => state.listOfActiveCalls,

  /**
   * Get call transfer status
   * @param {State} state - Current state
   * @returns {boolean} Whether a call transfer is in progress
   */
  getIsTransferringCall: (state) => state.isTransferringCall,
  getSnackbarState: (state) => state.snackbarState,
  getInterruptedCalls: (state) => state.interruptedCalls,
  getIsLoadingAnswerOrRejectCall: (state) => state.isLoadingAnswerOrRejectCall,
  getOutboundCalls: (state) => state.outboundCalls,
  getOutboundCallInProgress: (state) => (phoneNumber: string) => {
    const controller = state.outboundCalls[phoneNumber];
    if (!controller) return false;

    // Handle corrupted controllers from localStorage deserialization
    if (typeof controller.isCallActive === "function") {
      return controller.isCallActive();
    } else {
      // For corrupted controllers, assume they are active if they exist in the store
      return true;
    }
  },
  getOutboundCallByPhoneNumber: (state) => (phoneNumber: string) => {
    return state.outboundCalls[phoneNumber] || null;
  },
  hasActiveOutboundCalls: (state) => {
    return Object.keys(state.outboundCalls).length > 0;
  },
  getActiveOutboundCallCount: (state) => {
    return Object.values(state.outboundCalls).filter((controller) => {
      if (!controller) return false;

      // Handle corrupted controllers from localStorage deserialization
      if (typeof controller.isCallActive === "function") {
        return controller.isCallActive();
      } else {
        // For corrupted controllers, assume they are active if they exist in the store
        return true;
      }
    }).length;
  },
  getOutboundCallsAsAdapters: (state) => {
    // Return adapters for active outbound calls only
    const activeAdapterKeys = Object.keys(state.outboundCalls).filter(
      (phoneNumber) => {
        const controller = state.outboundCalls[phoneNumber];
        if (!controller) return false;

        // Handle corrupted controllers from localStorage deserialization
        if (typeof controller.isCallActive === "function") {
          return controller.isCallActive();
        } else {
          // For corrupted controllers, assume they are active if they exist in the store
          // This ensures component visibility is maintained after browser refresh
          console.warn(
            `Corrupted outbound controller detected for ${phoneNumber}, treating as active`
          );
          return true;
        }
      }
    );

    return activeAdapterKeys
      .map((phoneNumber) => state.outboundCallAdapters[phoneNumber])
      .filter(Boolean); // Filter out any undefined adapters
  },
  getOutboundCallLoadingState: (state) => (phoneNumber: string) => {
    return state.outboundCallLoadingStates[phoneNumber] || false;
  },
};

const mutations: MutationTree<State> = {
  addActiveCalls(state, activeCall: CallRoomController) {
    state.listOfActiveCalls.push(activeCall);
  },
  setIsTransferringCall(state, value: boolean) {
    state.isTransferringCall = value;
  },
  removeActiveCalls(state, activeCall: CallRoomController) {
    const index = state.listOfActiveCalls.findIndex(
      (call: CallRoomController) => {
        return call.room?.roomId === activeCall.room?.roomId;
      }
    );
    if (index !== -1) {
      state.listOfActiveCalls.splice(index, 1);
    }
  },
  setSnackbarState(state, snackbarState: SnackbarState) {
    state.snackbarState = snackbarState;
  },
  setInterruptedCalls(state, interruptedCalls: CallRoomController[]) {
    state.interruptedCalls = interruptedCalls;
  },
  setActiveCalls(state, activeCalls: CallRoomController[]) {
    state.listOfActiveCalls = activeCalls;
  },
  setIsLoadingAnswerOrRejectCall(state, value: boolean) {
    state.isLoadingAnswerOrRejectCall = value;
  },
  resetTemporaryState(state) {
    state.isLoadingAnswerOrRejectCall = false;
    state.isTransferringCall = false;
    state.snackbarState = {
      isActive: false,
      color: "",
      message: "",
      timeout: -1,
    };
    state.outboundCallLoadingStates = {};
    // state.outboundCalls = {};
    // state.outboundCallAdapters = {};
  },
  setOutboundCall(
    state,
    {
      controller,
      phoneNumber,
    }: { controller: OutboundCallController; phoneNumber: string }
  ) {
    // Use Vue.set for proper reactivity
    Vue.set(state.outboundCalls, phoneNumber, controller);
    // Create and store the adapter instance for this outbound call
    const adapter = new OutboundCallControllerAdapter(controller);
    Vue.set(state.outboundCallAdapters, phoneNumber, adapter);
  },
  removeOutboundCall(state, phoneNumber: string) {
    delete state.outboundCalls[phoneNumber];
    // Also clean up loading state and adapter when removing outbound call
    Vue.delete(state.outboundCallLoadingStates, phoneNumber);
    Vue.delete(state.outboundCallAdapters, phoneNumber);
  },
  clearAllOutboundCalls(state) {
    state.outboundCalls = {};
    state.outboundCallLoadingStates = {};
    state.outboundCallAdapters = {};
  },

  setOutboundCallLoading(
    state,
    { phoneNumber, loading }: { phoneNumber: string; loading: boolean }
  ) {
    Vue.set(state.outboundCallLoadingStates, phoneNumber, loading);
  },
  removeOutboundCallLoading(state, phoneNumber: string) {
    Vue.delete(state.outboundCallLoadingStates, phoneNumber);
  },
};

const actions: ActionTree<State, any> = {
  addActiveCalls(context, activeCall: CallRoomController) {
    context.commit("addActiveCalls", activeCall);
  },
  setIsTransferringCall(context, value: boolean) {
    context.commit("setIsTransferringCall", value);
  },
  removeActiveCalls(context, activeCall: CallRoomController) {
    context.commit("removeActiveCalls", activeCall);
  },
  /**
   * Answer an incoming call
   * @param {Object} param0 - Vuex action context
   * @param {CallRoomController} controller - Call controller for the incoming call
   * @returns {Promise<CallRoomController>} The joined call controller
   * @throws {Error} If joining the call room fails
   */
  async answerCall(
    { dispatch, commit, state, rootGetters },
    controller: CallRoomController
  ) {
    try {
      commit("setIsLoadingAnswerOrRejectCall", true);
      // Check if there is already active outbound call
      if (Object.keys(state.outboundCalls).length > 0) {
        throw new Error(ERROR_REASON.CALL_LIMITATION_ERROR);
      }
      // Check if there is already an active call
      if (state.listOfActiveCalls.length > 0) {
        throw new Error(ERROR_REASON.CALL_LIMITATION_ERROR);
      }
      if (!rootGetters.getCsRole.toLowerCase().includes("cs")) {
        throw new Error(ERROR_REASON.ROLE_NOT_ALLOWED);
      }
      // Connect to the call room
      const res = await controller.connectToRoom();
      if (res instanceof Error)
        throw new Error(`Failed join room: ${res.message}`);
      // Update the call session status to answered
      controller.session.status = CallSessionStatus.ANSWERED;
      // Add the call to the list of active calls
      dispatch("addActiveCalls", controller);
      return controller;
    } catch (error) {
      if ((error as Error).message.includes(ERROR_REASON.MIC_DENIED)) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: "$vuetify.inboundCallMicPermissionErrorMessage",
          timeout: -1,
          reason: ERROR_REASON.MIC_DENIED,
        });
      } else if (
        (error as Error).message.includes(ERROR_REASON.MIC_NOT_GRANTED)
      ) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: "$vuetify.inboundCallMicPermissionErrorMessage",
          timeout: -1,
          reason: ERROR_REASON.MIC_NOT_GRANTED,
        });
      } else if (
        (error as Error).message.includes(ERROR_REASON.CALL_LIMITATION_ERROR)
      ) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: i18n.t("$vuetify.inboundCallActiveCallLimitationError"),
          timeout: -1,
        });
      } else if (
        (error as Error).message.includes(ERROR_REASON.SESSION_NOT_RINGING)
      ) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: i18n.t("$vuetify.inboundCallPickupByAnotherAgent"),
          timeout: -1,
          reason: ERROR_REASON.SESSION_NOT_RINGING,
        });
      } else if (
        (error as Error).message.includes(ERROR_REASON.ROLE_NOT_ALLOWED)
      ) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: i18n.t("$vuetify.inboundCallRoleNotAllowedAnswer"),
          timeout: -1,
          reason: ERROR_REASON.ROLE_NOT_ALLOWED,
        });
      } else {
        dispatch(
          "setSnackbarTop",
          {
            color: "#FFD4D6",
            isActive: true,
            message: i18n.t("$vuetify.errorAnswerCall"),
          },
          { root: true }
        );
      }
      return new Error(`Failed answer call: ${error}`);
    } finally {
      commit("setIsLoadingAnswerOrRejectCall", false);
    }
  },

  /**
   * Disconnect from an active call
   * @param {Object} param0 - Vuex action context
   * @param {CallRoomController} controller - Call controller to disconnect
   */
  async disconnectCall(
    { commit, dispatch, state },
    controller: CallRoomController
  ) {
    try {
      const res = await controller.disconnectFromRoom();
      if (res instanceof Error)
        throw new Error(`Failed disconnect room: ${res.message}`);

      // Check if this is an outbound call adapter and clean up the outbound calls map
      if (
        (controller as any).isOutboundCall &&
        (controller as any).isOutboundCall()
      ) {
        const adapter = controller as any;
        const phoneNumber = adapter.room.customerPhoneNumber;
        if (phoneNumber && state.outboundCalls[phoneNumber]) {
          commit("removeOutboundCall", phoneNumber);
          return; // Outbound calls are not in listOfActiveCalls
        }
      } else {
        commit("removeActiveCalls", controller);
      }
    } catch (error) {
      console.error("ERROR DISCONNECT CALL ", error);
      dispatch(
        "setSnackbarTop",
        {
          color: "#FFD4D6",
          isActive: true,
          message: `Error terminate the call: ${error}`,
        },
        { root: true }
      );
    }
  },

  /**
   * Put an active call on hold
   * @param {Object} param0 - Vuex action context
   * @param {CallRoomController} controller - Call controller to put on hold
   */
  async holdCall({ dispatch }, controller: CallRoomController) {
    try {
      const res = await controller.toggleHoldState();
      if (res instanceof Error)
        throw new Error(`Failed hold call: ${res.message}`);
    } catch (error) {
      console.error("ERROR HOLD CALL ", error);
      dispatch(
        "setSnackbarTop",
        {
          color: "#FFD4D6",
          isActive: true,
          message: `${i18n.t("$vuetify.errorHoldCall")}: ${error}`,
        },
        { root: true }
      );
    }
  },
  async initiateOutboundCall(
    { commit, state },
    {
      controller,
      phoneNumber,
    }: { controller: OutboundCallController; phoneNumber: string }
  ) {
    try {
      // Set loading state
      commit("setOutboundCallLoading", { phoneNumber, loading: true });

      // Check if there is already an active outbound call to this phone number
      if (state.outboundCalls[phoneNumber]) {
        throw new Error(ERROR_REASON.CALL_LIMITATION_ERROR);
      }

      // Store the controller reference
      commit("setOutboundCall", { controller, phoneNumber });

      // Initiate the call
      await controller.initiateCall();

      // Clear loading state on success
      commit("removeOutboundCallLoading", phoneNumber);
    } catch (error) {
      console.error("Error in initiateOutboundCall", { phoneNumber, error });

      if (
        (error as Error).message.includes(ERROR_REASON.CALL_LIMITATION_ERROR)
      ) {
        commit("setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: i18n.t("$vuetify.inboundCallActiveCallLimitationError"),
          timeout: -1,
        });
      }
      // Clean up on error
      commit("removeOutboundCall", phoneNumber);
      commit("removeOutboundCallLoading", phoneNumber);
      throw error;
    }
  },
  setSnackbarState(context, snackbarState: SnackbarState) {
    context.commit("setSnackbarState", snackbarState);
  },
  setActiveCalls(context, activeCalls: CallRoomController[]) {
    context.commit("setActiveCalls", activeCalls);
  },
  setInterruptedCalls(context, interruptedCalls: CallRoomController[]) {
    context.commit("setInterruptedCalls", interruptedCalls);
  },
  disconnectAllInterruptedCalls(context) {
    context.state.interruptedCalls.forEach((call) => {
      if (
        call &&
        typeof call === "object" &&
        call.room &&
        call.room.type === RoomType.WHATSAPP
      ) {
        call = new CallRoomWhatsappController(call.room, call.session);
        context.dispatch("disconnectCall", call);
      }
    });
    context.commit("setInterruptedCalls", []);
  },
  disconnectAllActiveCalls(context) {
    context.state.listOfActiveCalls.forEach((call) => {
      context.dispatch("disconnectCall", call);
    });
    context.commit("setActiveCalls", []);
  },
  disconnectAllOutboundCalls(context) {
    Object.values(context.state.outboundCalls).forEach((controller) => {
      if (controller) {
        // Create adapter for consistent interface and call disconnectCall
        const adapter = new OutboundCallControllerAdapter(controller);
        context.dispatch("disconnectCall", adapter);
      }
    });
    context.commit("clearAllOutboundCalls");
  },
  resetTemporaryState(context) {
    context.commit("resetTemporaryState");
  },
  setIsLoadingAnswerOrRejectCall(context, value: boolean) {
    context.commit("setIsLoadingAnswerOrRejectCall", value);
  },
};
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
